import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ChevronRight, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { FaEye } from "react-icons/fa";
import { trpc } from "@/app/api/trpcClient";
import { Avatar } from "@/app/components/Avatar";
import { ContextModal } from "@/app/components/ContextModal";
import { LoadingCard } from "@/app/components/LoadingCard";
import { MemberBadges } from "@/app/components/MemberBadges";
import { MemberContent } from "@/app/components/MemberContent";
import { useAccessLogger } from "@/app/hooks/useAccessLogger";
import { useDagStore } from "@/app/hooks/useDagStore";
import { useDataSync } from "@/app/hooks/useDataSync";

export const Route = createFileRoute("/member/$memberId")({
  component: MemberProfilePage,
});

function MemberProfilePage() {
  const { memberId } = Route.useParams();
  const navigate = useNavigate();
  const { isLoading } = useDataSync();
  const member = useDagStore((state) => state.teamMembers.find((m) => m.id === memberId));
  const utils = trpc.useUtils();
  const [contextModalOpen, setContextModalOpen] = useState(false);
  const { trackPageView, trackButtonClick } = useAccessLogger();

  const { mutate: onViewMember } = trpc.people.onViewMember.useMutation({
    onSuccess: (data, variables) => {
      // Update the query cache with the new view count
      utils.people.getMemberViewCount.setData(
        { memberId: variables.memberId },
        { viewCount: data.viewCount },
      );
    },
  });

  // Fetch view count with polling for real-time updates
  const viewCountQuery = trpc.people.getMemberViewCount.useQuery(
    { memberId: memberId },
    {
      enabled: !!memberId,
      refetchInterval: 5000, // Poll every 5 seconds
    },
  );

  // Track member views when page loads
  useEffect(() => {
    if (memberId) {
      onViewMember({
        type: "member",
        memberId: memberId,
      });
      trackPageView("MemberProfilePage", { memberId });
    }
  }, [memberId, onViewMember, trackPageView]);

  const handleOpenContext = () => {
    if (!member) return;
    trackButtonClick("member-profile:context", { memberId: member.id });
    setContextModalOpen(true);
  };

  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate({ to: "/" });
    }
  };

  if (!member) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-4 mb-6">
          <button
            type="button"
            onClick={handleBackNavigation}
            className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
        </div>
        <LoadingCard
          isLoading={isLoading}
          className="bg-white border border-stone-200 rounded-xl p-8 text-center"
        >
          <p className="text-stone-600">Member not found</p>
        </LoadingCard>
      </div>
    );
  }

  // Use query result as the source of truth (updated by mutation and polling)
  const viewCount = viewCountQuery.data?.viewCount ?? member.viewCount ?? 0;
  const workWithMeUrl = member.workWithMeUrl ?? member.notionUsers[0]?.workWithMeUrl ?? null;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <button
          type="button"
          onClick={handleBackNavigation}
          className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>
      </div>

      {/* Member Profile Card */}
      <div className="bg-white border border-stone-200 rounded-xl p-8 shadow-sm">
        {/* Member Header */}
        <div className="flex items-start gap-4 mb-6 pb-6 border-b border-stone-200">
          <Avatar member={member} size="lg" />
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-stone-900">{member.name}</h1>
              {member.pageLink && (
                <a
                  href={member.pageLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-stone-500 hover:text-stone-700"
                  title="View team task view in Notion"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              )}
              <div className="flex-1" />
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium bg-stone-100 text-stone-600">
                <FaEye className="w-4 h-4" />
                {viewCount}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <MemberBadges role={member.role} team={member.team} />
              {workWithMeUrl && (
                <a
                  href={workWithMeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    trackButtonClick("member-profile:work-with-me", {
                      memberId: member.id,
                    });
                  }}
                >
                  Work With Me
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
              {member.context && (
                <button
                  type="button"
                  onClick={handleOpenContext}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-stone-600 bg-stone-100 rounded-md hover:bg-stone-200 transition-colors"
                >
                  <span>Context</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Member Content */}
        <MemberContent member={member} variant="page" />
      </div>

      {/* Context Modal */}
      {member?.context && (
        <ContextModal
          open={contextModalOpen}
          onOpenChange={setContextModalOpen}
          title={member.name}
          content={member.context}
        />
      )}
    </div>
  );
}
